import React from 'react';
import { ProductItem, RiskLevel } from '../../types/assetAllocation';
import { cn } from '@/lib/utils';

interface ProductCardProps {
  type: string;
  items: ProductItem[];
  getRiskLevelColor: (level: RiskLevel) => string;
  mapLevelToHeader: (level: RiskLevel) => string;
}

const ProductCard: React.FC<ProductCardProps> = ({
  type,
  items,
  getRiskLevelColor,
  mapLevelToHeader
}) => {
  const getHeaderStyle = () => {
    switch (items[0]?.level) {
      case 'R1':
        return 'bg-[rgba(240,253,244,0.60)] border-green-200';
      case 'R2':
        return 'bg-[linear-gradient(267deg,#EEEDFF_4.19%,#F6F7FF_86.14%)] border-blue-200';
      case 'R3':
        return 'bg-[linear-gradient(267deg,#FFF7E6_4.19%,#FFF1E6_86.14%)] border-orange-200';
      case 'R4':
        return 'bg-[linear-gradient(90deg,#FFF8FB_0%,#FFF0F6_100%)] border-red-200';
      case 'R5':
        return 'bg-[linear-gradient(90deg,#FFF8FB_0%,#FFF0F6_100%)] border-red-200';
      default:
        return 'bg-[linear-gradient(90deg,#FFF8FB_0%,#FFF0F6_100%)] border-gray-200';
    }
  };

  const getBorderClassName = () => {
    switch (items[0]?.level) {
      case 'R1':
        return 'bg-green-600/40';
      case 'R2':
        return 'bg-[linear-gradient(-90deg,#3215F3,#ECB1FF)]';
      case 'R3':
        return 'bgR3';
      case 'R4':
        return 'bg-[linear-gradient(-90deg,#FF7875,#FFD7DF)]';
      case 'R5':
        return 'bgR5';
      default:
        return 'bg-[linear-gradient(-90deg,#3215F3,#ECB1FF)]';
    }
  };

  const getTagStyle = () => {
    const color = getRiskLevelColor(items[0]?.level as RiskLevel);
    switch (color) {
      case 'green':
        return 'bg-green-100 text-green-800';
      case 'blue':
        return 'bg-blue-100 text-blue-800';
      case 'orange':
        return 'bg-orange-100 text-orange-800';
      case 'red':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="relative rounded-lg bg-white p-[2px] pb-3">
      <div className={cn('absolute inset-0 rounded-lg p-[1px]', getBorderClassName())}>
        <div className="h-full w-full rounded-lg bg-white"></div>
      </div>
      <div className="relative z-10 overflow-hidden rounded-lg bg-white">
        <div className={`${getHeaderStyle()} flex items-center justify-between px-3 py-2`}>
          <span className="text-sm font-semibold text-[#1F1F1F]">{type}</span>
        </div>

        {items.map(item => (
          <div className="p-3" key={item.product_name}>
            <div className="space-y-3">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-base font-semibold text-[#1F1F1F]">
                    {item.product_name}
                  </span>
                  <div className="flex gap-1">
                    <span className={`rounded px-2 py-0.5 text-xs ${getTagStyle()}`}>
                      {mapLevelToHeader(item.level as RiskLevel)}
                    </span>
                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {item.subtype}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-start gap-2 text-xs text-[#8C8C8C]">
                  <span>建议配置比例{item.proportion}</span>
                  <div className="h-3 w-px bg-[#F0F0F0]"></div>
                  <span>赎回费{item.historical_return_1y}</span>
                  <div className="h-3 w-px bg-[#F0F0F0]"></div>
                  <span>最大回撤{item.max_drawdown_1y}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <div className="flex items-center justify-start gap-1">
                    <span className="text-base font-semibold text-[#F5222D]">
                      {item.historical_return_1y}
                    </span>
                  </div>
                  <div className="flex items-center justify-start gap-1">
                    <span className="text-xs text-[#1F1F1F]">近一年收益率</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-start gap-1">
                    <span className="text-base font-semibold text-[#1F1F1F]">
                      {item.max_drawdown_1y}
                    </span>
                  </div>
                  <div className="flex items-center justify-start gap-1">
                    <span className="text-xs text-[#1F1F1F]">最大回撤</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductCard;
